import { useEffect } from "react";
import { useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import {
  AnchorButton,
  Button,
  Callout,
  Dialog,
  FormGroup,
  Icon,
  ProgressBar,
} from "@blueprintjs/core";
import {
  faCheckCircle,
  faExclamationCircle,
  faTimesCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { MetadataRow } from "@pages/editor/components/RuleMetadataEditor/RuleMetadataEditor";
import { Workbench } from "@pages/editor/stores";

import { PublishMetadata } from "./PublishMetadata";

const Actions = styled.div`
  gap: 16px;
  display: flex;
  justify-content: flex-end;
`;

const CleanDialog = styled(Dialog)`
  width: 800px;
  overflow: hidden;
  padding: 0;
`;

export const PublicRuleChecklistContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  font-size: var(--global-font-size-s);
`;

const IntervalStep = 100;
const IntervalDuration = 8000;

function PublicRuleChecklistComponent({
  onCreatePRClick,
  workbench,
  onClose,
}: {
  onCreatePRClick: () => void;
  workbench: Workbench;
  onClose: () => void;
}) {
  const [state, setState] = useState<"meta-data" | "confirmation" | "loading">(
    "meta-data"
  );
  const [timer, setTimer] = useState<number>(0);
  const timerRef = useRef<number>(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function startLoader() {
    setTimer(0);
    timerRef.current = 0;
    function nextInterval() {
      timeoutRef.current = setTimeout(() => {
        if (timerRef.current > IntervalDuration) {
          onCreatePRClick();
        } else {
          const newTime = timerRef.current + IntervalStep;
          timerRef.current = newTime;
          setTimer(newTime);
          nextInterval();
        }
      }, IntervalStep);
    }
    setState("loading");
    nextInterval();
  }

  useEffect(() => {
    return () => {
      // On unmount, cancel the timer
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  function cancelLoader() {
    setTimer(0);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setState("meta-data");
  }

  const expectedMatches = workbench.bundle?.expectedMatches.length || 0;
  const expectedNotMatches = workbench.bundle?.expectedNotMatches.length || 0;

  return (
    <CleanDialog
      isOpen={true}
      onClose={onClose}
      title={state === "meta-data" ? "Publish rule" : "Creating Pull Request"}
    >
      <PublicRuleChecklistContainer>
        {state === "meta-data" && (
          <>
            <Callout title="Ready for prime time?" intent="primary">
              In the Semgrep Registry anyone can find what you've created. To
              publish, let's make sure that your metadata is ready for prime
              time!
            </Callout>
            <PublishMetadata workbench={workbench} />
            <MetadataRow>
              <FormGroup inline label="Test cases">
                {expectedMatches && expectedNotMatches ? (
                  <div>
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      color="var(--global-green)"
                    />{" "}
                    Detected
                  </div>
                ) : expectedMatches ? (
                  <div>
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      color="var(--global-warning-color)"
                    />{" "}
                    Only postive test case is detected, add a negative case
                  </div>
                ) : expectedNotMatches ? (
                  <div>
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      color="var(--global-warning-color)"
                    />{" "}
                    Only postive test case is detected, add a negative case
                  </div>
                ) : (
                  <div>
                    <FontAwesomeIcon
                      icon={faTimesCircle}
                      color="var(--global-red)"
                    />{" "}
                    Not found{" "}
                    <a href="https://github.com/returntocorp/semgrep-rules/blob/develop/CONTRIBUTING.md#tests">
                      (how to add?)
                    </a>
                  </div>
                )}
              </FormGroup>
            </MetadataRow>

            <Actions>
              <AnchorButton
                href="https://github.com/returntocorp/semgrep-rules#contributing"
                outlined
                intent="primary"
                icon={<Icon icon="book" />}
              >
                Contributing guide
              </AnchorButton>
              <Button
                intent="primary"
                icon={<FontAwesomeIcon icon={faCheckCircle} />}
                onClick={() => {
                  if (workbench.hasUnsavedChanges) {
                    workbench.saveBundle();
                  }
                  setState("confirmation");
                }}
              >
                {workbench.hasUnsavedChanges ? "Save and continue" : "Continue"}
              </Button>
            </Actions>
          </>
        )}
        {state === "confirmation" && (
          <>
            <div>
              <Callout title="Beware, public repository ahead" intent="warning">
                We'll create a PR in a public repo. This means that your rule
                will be public too and can not ever be deleted.
              </Callout>
              <div
                style={{
                  marginTop: "16px",
                }}
              >
                Next, our Semgrep PR bot will create a pull request on GitHub in
                the{" "}
                <a href="https://github.com/returntocorp/semgrep-rules">
                  semgrep-rules repo.{" "}
                </a>
                If you publish, your rule will be open sourced under{" "}
                <a href="https://github.com/returntocorp/semgrep-rules/blob/develop/LICENSE">
                  LGPL 2.1
                </a>
                .
              </div>
            </div>
            <Actions>
              <Button
                intent="primary"
                icon={<Icon icon="git-pull" color="white" />}
                onClick={() => {
                  startLoader();
                }}
              >
                Create PR
              </Button>
            </Actions>
          </>
        )}
        {state === "loading" && (
          <>
            <div>
              <ProgressBar
                intent="primary"
                value={(timer || 0) / IntervalDuration}
              />
            </div>
            <Actions>
              <Button
                outlined
                intent="primary"
                // icon={<Icon icon="git-pull" />}
                onClick={cancelLoader}
              >
                Cancel
              </Button>
            </Actions>
          </>
        )}
      </PublicRuleChecklistContainer>
    </CleanDialog>
  );
}

export const PublicRuleChecklist = observer(PublicRuleChecklistComponent);
